/**
 * AML Service API Client
 * Frontend service for AML (Anti-Money Laundering) operations
 *
 * TASK-251: Frontend AML service using httpClient
 *
 * SECURITY: All requests go through gateway (port 8000)
 * Uses httpClient for authentication and account isolation
 */

import { httpClient, httpClientUpload } from '../lib/httpClient';

// =============================================================================
// Type Definitions
// =============================================================================

export type AMLRiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';
export type AMLAlertStatus = 'New' | 'Investigating' | 'Cleared' | 'Reported';
export type AMLAlertRole = 'Front Office' | 'Compliance' | 'Legal';

export interface ValidationFinding {
  category: string;
  description: string;
  severity: AMLRiskLevel;
}

export interface DocumentAnalysisResult {
  fileName: string;
  uploadDate: string;
  riskScore: number;
  riskLevel: AMLRiskLevel;
  documentTypeDetected: string;
  findings: ValidationFinding[];
  summary: string;
  isStolenOrStock: boolean;
  onlineMatches: string[];
  imageHash?: string;
}

export interface ReverseSearchResult {
  hasMatches: boolean;
  urls: string[];
  fullMatches: string[];
  partialMatches: string[];
  visuallySimilar: string[];
  bestGuessLabels: string[];
  isStockImage: boolean;
  matchCount: number;
}

export interface MonitoringRule {
  id: string;
  ruleName: string;
  criteria: string;
  riskSeverity: AMLRiskLevel;
  sourceRegulationId?: string;
}

export interface Regulation {
  id: string;
  source: string;
  title: string;
  summary: string;
  rawContent: string;
  date: string;
  impactLevel: AMLRiskLevel;
  active: boolean;
  links: string[];
  extractedRules?: MonitoringRule[];
}

export interface AMLTransaction {
  id: string;
  clientId?: string;
  clientName?: string;
  amount: number;
  currency: string;
  type: 'inbound' | 'outbound' | 'internal';
  counterparty: string;
  counterpartyCountry: string;
  date: string;
  reference?: string;
  riskScore?: number;
  riskLevel?: AMLRiskLevel;
  riskReasoning?: string;
  status: string;
}

export interface TransactionAnalysisResult {
  riskScore: number;
  riskLevel: AMLRiskLevel;
  reasoning: string;
}

export interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  assignedRole: AMLAlertRole;
  estimatedDuration: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  completedAt?: string;
  completedBy?: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  forRiskLevel: AMLRiskLevel;
  steps: WorkflowStep[];
  autoTrigger: boolean;
  requiresApproval: boolean;
  approvalRole?: AMLAlertRole;
}

export interface WorkflowInstance {
  id: string;
  templateId: string;
  templateName: string;
  alertId: string;
  startedAt: string;
  startedBy: string;
  status: 'in_progress' | 'completed' | 'cancelled';
  currentStepIndex: number;
  steps: WorkflowStep[];
}

export interface AuditEntry {
  id: string;
  alertId: string;
  action: string;
  details: string;
  performedBy: string;
  timestamp: string;
  workflowStepId?: string;
}

export interface RSSSource {
  id: string;
  name: string;
  country: string;
  type: string;
  url: string;
}

// =============================================================================
// Document Analysis API
// =============================================================================

/**
 * Analyze a document for AML risk factors
 * Uses GPT-4o vision and optional Google Vision reverse search
 */
export const analyzeDocument = async (
  file: File,
  includeReverseSearch: boolean = true
): Promise<DocumentAnalysisResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  params.append('include_reverse_search', String(includeReverseSearch));

  // Must clear Content-Type to let axios set multipart/form-data with boundary
  const response = await httpClientUpload.post<DocumentAnalysisResult>(
    `/api/aml/documents/analyze?${params.toString()}`,
    formData,
    {
      headers: {
        'Content-Type': undefined as unknown as string,
      },
    }
  );
  return response;
};

/**
 * Compute perceptual hash for a document
 */
export const computeDocumentHash = async (file: File): Promise<{
  filename: string;
  hash: string;
  all_hashes: Record<string, string>;
  image_info: {
    width: number;
    height: number;
    format: string;
    file_size: number;
    has_exif: boolean;
  };
}> => {
  const formData = new FormData();
  formData.append('file', file);

  return httpClientUpload.post('/api/aml/documents/hash', formData, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
};

/**
 * Compare two documents for similarity
 */
export const compareDocuments = async (file1: File, file2: File): Promise<{
  file1: string;
  file2: string;
  similarity: number;
  is_similar: boolean;
  threshold: number;
}> => {
  const formData = new FormData();
  formData.append('file1', file1);
  formData.append('file2', file2);

  return httpClientUpload.post('/api/aml/documents/compare', formData, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
};

/**
 * Perform reverse image search on a document
 */
export const reverseImageSearch = async (file: File): Promise<ReverseSearchResult> => {
  const formData = new FormData();
  formData.append('file', file);

  return httpClientUpload.post('/api/aml/documents/reverse-search', formData, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
};

// =============================================================================
// Regulations API
// =============================================================================

/**
 * List all regulations
 */
export const listRegulations = async (params?: {
  skip?: number;
  limit?: number;
  source?: string;
  impactLevel?: AMLRiskLevel;
}): Promise<{
  regulations: Regulation[];
  total: number;
}> => {
  const queryParams = new URLSearchParams();
  if (params?.skip) queryParams.append('skip', String(params.skip));
  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.source) queryParams.append('source', params.source);
  if (params?.impactLevel) queryParams.append('impact_level', params.impactLevel);

  return httpClient.get(`/api/aml/regulations/?${queryParams.toString()}`);
};

/**
 * Fetch regulations from an RSS feed
 */
export const fetchRSSFeed = async (
  source: string,
  forceRefresh: boolean = false
): Promise<{
  success: boolean;
  source: string;
  source_name: string;
  entries_fetched: number;
  entries_stored: number;
  entries: any[];
}> => {
  return httpClient.post('/api/aml/regulations/fetch-rss', {
    source
  }, {
    params: { force_refresh: forceRefresh }
  });
};

/**
 * Extract monitoring rules from regulation text
 */
export const extractRules = async (
  text?: string,
  regulationId?: string
): Promise<{
  title: string;
  summary: string;
  impactLevel: AMLRiskLevel;
  extractedRules: MonitoringRule[];
}> => {
  const params = regulationId ? { regulation_id: regulationId } : {};

  return httpClient.post('/api/aml/regulations/extract-rules', {
    text
  }, { params });
};

/**
 * List all monitoring rules
 */
export const listMonitoringRules = async (
  riskSeverity?: AMLRiskLevel
): Promise<{
  rules: MonitoringRule[];
  total: number;
}> => {
  const params = riskSeverity ? `?risk_severity=${riskSeverity}` : '';
  return httpClient.get(`/api/aml/regulations/monitoring-rules${params}`);
};

/**
 * List available RSS sources
 */
export const listRSSSources = async (): Promise<{
  sources: RSSSource[];
  total: number;
}> => {
  return httpClient.get('/api/aml/regulations/rss-sources');
};

/**
 * Create a new regulation manually
 */
export const createRegulation = async (data: {
  title: string;
  summary: string;
  raw_content: string;
  source: string;
  impact_level?: AMLRiskLevel;
  links?: string[];
}): Promise<{
  success: boolean;
  regulation: Regulation;
}> => {
  return httpClient.post('/api/aml/regulations/', data);
};

/**
 * Delete a regulation
 */
export const deleteRegulation = async (regulationId: string): Promise<{
  success: boolean;
  deleted_id: string;
}> => {
  return httpClient.delete(`/api/aml/regulations/${regulationId}`);
};

// =============================================================================
// Transaction Analysis API
// =============================================================================

/**
 * Analyze a transaction for AML risk
 */
export const analyzeTransaction = async (
  transaction: AMLTransaction,
  regulationIds?: string[]
): Promise<TransactionAnalysisResult> => {
  return httpClient.post('/api/aml/transactions/analyze', {
    transaction,
    regulationIds
  });
};

/**
 * Batch analyze multiple transactions
 */
export const analyzeTransactionsBatch = async (
  transactions: AMLTransaction[],
  regulationIds?: string[]
): Promise<{
  results: TransactionAnalysisResult[];
  summary: {
    total_analyzed: number;
    average_risk_score: number;
    max_risk_score: number;
    min_risk_score: number;
    risk_distribution: Record<AMLRiskLevel, number>;
    high_risk_count: number;
    flagged_transactions: any[];
  };
}> => {
  return httpClient.post('/api/aml/transactions/analyze-batch', {
    transactions,
    regulationIds
  });
};

/**
 * Quick transaction risk check (without AI)
 */
export const quickRiskCheck = async (params: {
  amount: number;
  currency?: string;
  counterparty_country?: string;
  transaction_type?: string;
}): Promise<{
  risk_score: number;
  risk_level: AMLRiskLevel;
  factors: string[];
  requires_review: boolean;
}> => {
  const queryParams = new URLSearchParams();
  queryParams.append('amount', String(params.amount));
  if (params.currency) queryParams.append('currency', params.currency);
  if (params.counterparty_country) queryParams.append('counterparty_country', params.counterparty_country);
  if (params.transaction_type) queryParams.append('transaction_type', params.transaction_type);

  return httpClient.post(`/api/aml/transactions/quick-check?${queryParams.toString()}`, {});
};

/**
 * Get risk threshold configuration
 */
export const getRiskThresholds = async (): Promise<{
  thresholds: Record<string, { min: number; max: number; description: string }>;
  reporting_thresholds: Record<string, number>;
  high_risk_countries: string[];
}> => {
  return httpClient.get('/api/aml/transactions/risk-thresholds');
};

// =============================================================================
// Workflow API
// =============================================================================

/**
 * List workflow templates
 */
export const listWorkflowTemplates = async (
  riskLevel?: AMLRiskLevel
): Promise<{
  templates: WorkflowTemplate[];
  total: number;
}> => {
  const params = riskLevel ? `?risk_level=${riskLevel}` : '';
  return httpClient.get(`/api/aml/workflows/templates${params}`);
};

/**
 * Get a specific workflow template
 */
export const getWorkflowTemplate = async (templateId: string): Promise<WorkflowTemplate> => {
  return httpClient.get(`/api/aml/workflows/templates/${templateId}`);
};

/**
 * Start a workflow for an alert
 */
export const startWorkflow = async (
  alertId: string,
  templateId: string
): Promise<{
  success: boolean;
  workflow: WorkflowInstance;
}> => {
  return httpClient.post('/api/aml/workflows/start', {
    alertId,
    templateId
  });
};

/**
 * Get workflow instance details
 */
export const getWorkflow = async (workflowId: string): Promise<WorkflowInstance> => {
  return httpClient.get(`/api/aml/workflows/${workflowId}`);
};

/**
 * Get workflow for a specific alert
 */
export const getWorkflowByAlert = async (alertId: string): Promise<{
  workflow: WorkflowInstance | null;
  has_workflow: boolean;
}> => {
  return httpClient.get(`/api/aml/workflows/by-alert/${alertId}`);
};

/**
 * Complete a workflow step
 */
export const completeWorkflowStep = async (
  workflowId: string,
  stepId: string,
  notes?: string
): Promise<{
  success: boolean;
  workflow: WorkflowInstance;
}> => {
  return httpClient.post(
    `/api/aml/workflows/${workflowId}/steps/${stepId}/complete`,
    { notes }
  );
};

/**
 * Cancel a workflow
 */
export const cancelWorkflow = async (
  workflowId: string,
  reason: string
): Promise<{
  success: boolean;
  workflow: WorkflowInstance;
}> => {
  return httpClient.post(`/api/aml/workflows/${workflowId}/cancel`, {
    reason
  });
};

/**
 * List workflow instances
 */
export const listWorkflows = async (params?: {
  status?: 'in_progress' | 'completed' | 'cancelled';
  limit?: number;
}): Promise<{
  workflows: WorkflowInstance[];
  total: number;
}> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.limit) queryParams.append('limit', String(params.limit));

  return httpClient.get(`/api/aml/workflows/?${queryParams.toString()}`);
};

// =============================================================================
// Audit Trail API
// =============================================================================

/**
 * Get audit trail for an alert
 */
export const getAuditTrail = async (alertId: string): Promise<{
  alert_id: string;
  entries: AuditEntry[];
  total: number;
}> => {
  return httpClient.get(`/api/aml/workflows/audit-trail/${alertId}`);
};

/**
 * Add a manual audit entry
 */
export const addAuditEntry = async (
  alertId: string,
  action: string,
  details: string
): Promise<{
  success: boolean;
  entry: AuditEntry;
}> => {
  return httpClient.post('/api/aml/workflows/audit-trail', {
    alertId,
    action,
    details
  });
};

// =============================================================================
// Default Export
// =============================================================================

const amlService = {
  // Documents
  analyzeDocument,
  computeDocumentHash,
  compareDocuments,
  reverseImageSearch,

  // Regulations
  listRegulations,
  fetchRSSFeed,
  extractRules,
  listMonitoringRules,
  listRSSSources,
  createRegulation,
  deleteRegulation,

  // Transactions
  analyzeTransaction,
  analyzeTransactionsBatch,
  quickRiskCheck,
  getRiskThresholds,

  // Workflows
  listWorkflowTemplates,
  getWorkflowTemplate,
  startWorkflow,
  getWorkflow,
  getWorkflowByAlert,
  completeWorkflowStep,
  cancelWorkflow,
  listWorkflows,

  // Audit
  getAuditTrail,
  addAuditEntry
};

export default amlService;
